import { useOptionalConcierge } from '@/hooks/use-concierge-context';
import { useIsMobile } from '@/hooks/use-is-mobile';
import usePlanPermissions from '@rhg/hooks/use-plan-permissions';
import commonMessages from '@rhg/lang/messages/common';
import { Button, ButtonProps, SxProps, Theme } from '@rhg/libs/material';
import {
    CareplanFilled,
    CareplanOutline,
    DiscoverStarburst,
    HomeFilled,
    HomeOutline,
    ProfileFilled,
    ProfileOutline,
    SearchActive,
    SearchOutline,
} from '@rhg/libs/material/icons';
import { routes } from '@rhg/utils/routes';
import Link from 'next/link';
import { useIntl } from 'react-intl';
import NavButton from '../nav-button';
import { navItemsMessages } from './messages';

const styleNavIcon = {
    width: 24,
    height: 24,
};
const styleSearchNavIcon = {
    width: 24,
    height: 24,
    // SVG itself is 20x20
    padding: '2px',
};
const styleGuideNavIcon = {
    ...styleNavIcon,
    padding: '1.5px',
    // The icon hard codes its own colour, so opt back into the nav item's
    // active/inactive colour like every other nav icon.
    color: 'inherit',
};

interface NavItemsProps {
    sx?: SxProps<Theme>;
    onClick?: () => void;
    testId?: string;
    addSyntheticIds?: boolean;
}

const NavItems = ({
    testId = 'NAV',
    addSyntheticIds,
    ...buttonProps
}: NavItemsProps & Partial<ButtonProps>) => {
    const { formatMessage } = useIntl();
    const { canView: canViewPlan } = usePlanPermissions();
    /* The error boundary fallback renders the header above ConciergeProvider. */
    const isConciergeGAEnabled =
        useOptionalConcierge()?.isConciergeGAEnabled ?? false;
    const isMobile = useIsMobile('lg');

    /*
     * Concierge GA users get the plan tab presented as their "Guide". The route,
     * test IDs and tracking are deliberately unchanged - it is the same tab, so
     * existing analytics and synthetic monitoring keep working. The starburst
     * has no filled counterpart, so the guide tab reuses it for the active
     * state, which the nav item still marks with its underline and colour.
     */
    const planTabIcon = isConciergeGAEnabled ? (
        <DiscoverStarburst sx={styleGuideNavIcon} data-testid="NavGuideIcon" />
    ) : (
        <CareplanOutline sx={styleNavIcon} />
    );
    const planTabActiveIcon = isConciergeGAEnabled ? (
        planTabIcon
    ) : (
        <CareplanFilled sx={styleNavIcon} />
    );
    const planTabLabel = isConciergeGAEnabled
        ? navItemsMessages.guideTab
        : commonMessages['common.page.careplan'];

    return (
        <>
            <NavButton
                component={Link}
                href={routes.dashboard}
                startIcon={<HomeOutline sx={styleNavIcon} />}
                activeStateIcon={<HomeFilled sx={styleNavIcon} />}
                testId={testId}
                trackButton="HomeTab"
                altActiveHrefs={[routes.favorites, routes.recent]}
                {...(addSyntheticIds
                    ? { 'data-syntheticid': 'NavHomeButton' }
                    : {})}
                {...buttonProps}
            >
                {formatMessage(commonMessages['common.page.home'])}
            </NavButton>
            {canViewPlan && (
                <NavButton
                    component={Link}
                    href={routes.careplan}
                    startIcon={planTabIcon}
                    activeStateIcon={planTabActiveIcon}
                    testId={testId}
                    trackButton="PlanTab"
                    {...(addSyntheticIds
                        ? { 'data-syntheticid': 'NavPlanButton' }
                        : {})}
                    {...buttonProps}
                >
                    {formatMessage(planTabLabel)}
                </NavButton>
            )}
            <NavButton
                component={Link}
                href={routes.discover}
                startIcon={<SearchOutline sx={styleSearchNavIcon} />}
                activeStateIcon={<SearchActive sx={styleSearchNavIcon} />}
                testId={testId}
                trackButton="DiscoverTab"
                {...(addSyntheticIds
                    ? { 'data-syntheticid': 'NavDiscoverButton' }
                    : {})}
                {...buttonProps}
            >
                {formatMessage(commonMessages['common.page.discover'])}
            </NavButton>
            <NavButton
                component={Link}
                href={routes.profile}
                startIcon={<ProfileOutline sx={styleNavIcon} />}
                activeStateIcon={<ProfileFilled sx={styleNavIcon} />}
                altActiveHrefs={[
                    routes.completed,
                    routes.emotionsCheckInHistory,
                ]}
                trackButton="ProfileTab"
                testId={testId}
                {...(addSyntheticIds
                    ? { 'data-syntheticid': 'NavProfileButton' }
                    : {})}
                {...buttonProps}
            >
                {formatMessage(commonMessages['common.page.profile'])}
            </NavButton>
            {isMobile && (
                <Button
                    trackButton="UseMobileApp"
                    variant="outlined"
                    fullWidth
                    href={routes.external.appDownloadDeeplink}
                    sx={{ mt: 4 }}
                >
                    {formatMessage(navItemsMessages.useMobileAppCta)}
                </Button>
            )}
        </>
    );
};
export default NavItems;
